/**
 * Merge two prop objects the way a component that CLONES its child has to.
 *
 * ── WHY THIS EXISTS, AND WHAT IT COST NOT TO HAVE IT ──────────────────────────────────
 *
 * Three components in this design system clone the caller's own element instead of wrapping it
 * — `OverlayTrigger`, `MenuTrigger` and `CollapsibleTrigger`. Cloning is the right shape: a
 * wrapper would add a box to the layout and would put the aria attributes on a div while the
 * real control sat inside it announcing nothing.
 *
 * But cloning means two sets of props meet, and a plain spread resolves that collision wrongly
 * in three different ways at once:
 *
 *   onClick     the child's handler REPLACES the trigger's, so the overlay never opens
 *   className   the child's class REPLACES `saltOverlayTrigger`, so it loses its styling
 *   style       one object replaces the other wholesale
 *
 * The first of those shipped. `OverlayTrigger` used `{...getReferenceProps(), ...children.props}`
 * and a call site whose button had its own `onClick` produced an overlay that could not be
 * opened — no error, no warning, and every existing test passed because none of them attached a
 * handler to the trigger's child. It was found while porting `CollapsibleTrigger`, whose own
 * tests DID assert the className half of the same bug.
 *
 * So: handlers CHAIN, className concatenates, style merges, and anything else takes the second
 * value when it is defined. This mirrors the vendor's `mergeProps` exactly, which is the point —
 * it is not a cleverer merge, it is the one the call sites were already written against.
 */
type Props = Record<string, unknown>;

function chain(
  a: (...args: unknown[]) => unknown,
  b: (...args: unknown[]) => unknown,
): (...args: unknown[]) => void {
  return (...args: unknown[]) => {
    a(...args);
    b(...args);
  };
}

export function mergeProps(propsA: Props, propsB: Props): Props {
  const merged: Props = { ...propsA };

  for (const key of Object.keys(propsB)) {
    const a = merged[key];
    const b = propsB[key];

    if (typeof a === "function" && typeof b === "function" && key.startsWith("on")) {
      // Both run, in that order: the trigger's behaviour first, then the caller's.
      merged[key] = chain(a as (...args: unknown[]) => unknown, b as (...args: unknown[]) => unknown);
    } else if (typeof a === "string" && typeof b === "string" && key === "className") {
      merged[key] = `${a} ${b}`;
    } else if (
      key === "style" &&
      typeof a === "object" &&
      a !== null &&
      typeof b === "object" &&
      b !== null
    ) {
      merged[key] = { ...(a as object), ...(b as object) };
    } else {
      // `!== undefined` rather than a truthiness check: `aria-expanded={false}` and `tabIndex={0}`
      // are meaningful values that a truthy test would discard.
      merged[key] = b !== undefined ? b : a;
    }
  }

  return merged;
}
